import { injectable } from 'inversify';
import Long from 'long'
import { Message, Field, Type } from "protobufjs/light";
import { Result, ok } from '@xlearn/common'
import { ProtobufSerializer } from '@/app/state-serialization/infrastructure';
import * as Api from '@/app/state-serialization/api'
import { ExamTrackingState } from '../common'
import { ExamAttempt } from '../../domain';
import { GradingMethod } from '../api';

@injectable()
export class ExamStateProtobufSerializer implements Api.StateSerializationPlugin<ExamTrackingState[]> {

  key = { id: 4, name: { ns: 'suspend-data', name: 'exams' }, format: 'protobuf' }

  serialize(data: ExamTrackingState[]): Result<Uint8Array, Api.SerializerError> {

    const exams = data.map(exam => {

      const attempts = exam.attempts.map(x => this.serializeAttempt(x))

      return new ExamStateProtoMessage({
        id: exam.id,
        attempts: attempts,
        effectiveGrading: mapGrading(exam.effectiveGrading)
      })
    })

    const message = new ExamStatesProtoMessage({
      exams: exams
    })

    return ProtobufSerializer.serialize<ExamStatesProtoMessage>(message, ExamStatesProtoMessage)
    
    function mapGrading(input: GradingMethod): GradingMethodProtoEnum {
      switch (input) {
        case 'highest-grade':
          return GradingMethodProtoEnum.highestGrade
        case 'last-attempt':
          return GradingMethodProtoEnum.lastAttempt
      }
    }
  }

  deserialize(content: Uint8Array): Result<ExamTrackingState[], Api.DeserializerError> {

    const deserializeResult = ProtobufSerializer.deserialize<ExamStatesProtoMessage>(content,
                                                                                     ExamStatesProtoMessage)
    if (!deserializeResult.success) {
      return deserializeResult
    }

    const exams = deserializeResult.value.exams.map(exam => {

      const state: ExamTrackingState = {
        id: exam.id,
        attempts: exam.attempts.map(x => this.deserializeAttempt(x)),
        effectiveGrading: mapGrading(exam.effectiveGrading)
      }
      
      return state
    })

    return ok(exams)

    function mapGrading(input: GradingMethodProtoEnum): GradingMethod {
      switch (input) {
        case GradingMethodProtoEnum.highestGrade:
          return 'highest-grade'
        case GradingMethodProtoEnum.lastAttempt:
          return 'last-attempt'
      }
    }
  }

  private serializeAttempt(attempt: ExamAttempt): ExamAttemptProtoMessage {
    return new ExamAttemptProtoMessage({
      timestamp: ProtobufSerializer.serializeDate(attempt.timestamp),
      minScore: attempt.score.min,
      maxScore: attempt.score.max,
      rawScore: attempt.score.raw,
      passingScore: attempt.passingScore,
      result: mapResult(attempt.result),
      durationSeconds: Math.round(attempt.durationSeconds)
    })

    function mapResult(result: 'passed' | 'failed'): ResultProtoEnum {
      switch (result) {
        case 'passed':
          return ResultProtoEnum.passed
        case 'failed':
          return ResultProtoEnum.failed
      }
    }
  }

  private deserializeAttempt(attempt: ExamAttemptProtoMessage): ExamAttempt {
    const result: ExamAttempt = {
      timestamp: ProtobufSerializer.deserializeDate(attempt.timestamp),
      score: {
        min: attempt.minScore,
        max: attempt.maxScore,
        raw: attempt.rawScore
      },
      passingScore: attempt.passingScore,
      result: mapResult(attempt.result),
      durationSeconds: attempt.durationSeconds
    }

    return result

    function mapResult(result: ResultProtoEnum): 'passed' | 'failed' {
      switch (result) {
        case ResultProtoEnum.passed:
          return 'passed'
        case ResultProtoEnum.failed:
          return 'failed'
      }
    }
  }
}

enum ResultProtoEnum {
  passed = 0,
  failed = 1
}

enum GradingMethodProtoEnum {
  highestGrade = 0,
  lastAttempt = 1
}

@Type.d('ExamAttemptProtoMessage')
class ExamAttemptProtoMessage extends Message<ExamAttemptProtoMessage> {
  @Field.d(1, 'uint64', "optional", 0)
  timestamp: Long

  @Field.d(2, 'double', "optional", 0)
  minScore: number

  @Field.d(3, 'double', "optional", 0)
  maxScore: number

  @Field.d(4, 'double', "optional", 0)
  rawScore: number

  @Field.d(5, 'double', "optional", 0)
  passingScore: number

  @Field.d(6, ResultProtoEnum, "optional", ResultProtoEnum.failed)
  result: ResultProtoEnum;

  @Field.d(7, 'uint32', "optional", 0)
  durationSeconds: number
}

@Type.d('ExamStateProtoMessage')
class ExamStateProtoMessage extends Message<ExamStateProtoMessage> {

  @Field.d(1, 'string', "optional", '')
  id: string

  @Field.d(2, ExamAttemptProtoMessage, 'repeated')
  attempts: ExamAttemptProtoMessage[]

  @Field.d(3, GradingMethodProtoEnum, "optional", GradingMethodProtoEnum.highestGrade)
  effectiveGrading: GradingMethodProtoEnum;
}

@Type.d('ExamStatesProtoMessage')
class ExamStatesProtoMessage extends Message<ExamStatesProtoMessage> {

  @Field.d(1, 'uint32', 'optional', 1)
  public schemaVersion: number

  @Field.d(2, ExamStateProtoMessage, 'repeated')
  public exams: ExamStateProtoMessage[]
}

